/**
 * Audio Service
 * Handles playing notification sounds
 * Handles browser autoplay policies by initializing audio context on user interaction
 */

const NOTIFICATION_SOUND_PATH = '/notification-sound.wav';

class AudioService {
  private audioElement: HTMLAudioElement | null = null;
  private audioContext: AudioContext | null = null;
  private isInitialized: boolean = false;

  constructor() {
    // Initialize audio context on first user interaction
    if (typeof window !== 'undefined') {
      document.addEventListener('click', () => this.initializeAudioContext(), { once: true });
      document.addEventListener('touchstart', () => this.initializeAudioContext(), { once: true });
      document.addEventListener('keydown', () => this.initializeAudioContext(), { once: true });
    }
  }

  /**
   * Initialize Web Audio Context (required for autoplay policy)
   */
  private initializeAudioContext(): void {
    if (this.isInitialized) return;

    try {
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      if (audioContext.state === 'suspended') {
        audioContext.resume().then(() => {
          console.log('✅ Audio context resumed');
          this.audioContext = audioContext;
          this.isInitialized = true;
        });
      } else {
        this.audioContext = audioContext;
        this.isInitialized = true;
        console.log('✅ Audio context initialized');
      }
    } catch (err) {
      console.error('Failed to initialize audio context:', err);
    }
  }

  /**
   * Initialize audio element
   */
  private initAudio(): void {
    if (this.audioElement) return;

    try {
      const audio = new Audio();
      audio.src = NOTIFICATION_SOUND_PATH;
      audio.volume = 1.0;
      audio.preload = 'auto';
      audio.crossOrigin = 'anonymous';

      audio.addEventListener('error', (e) => {
        console.error('Audio element error:', (e.target as HTMLAudioElement).error?.message);
      });

      this.audioElement = audio;
    } catch (err) {
      console.error('Failed to initialize audio element:', err);
    }
  }

  /**
   * Play notification sound
   */
  async playNotificationSound(): Promise<void> {
    try {
      // Initialize audio context if not done yet
      if (!this.isInitialized) {
        this.initializeAudioContext();
        // Wait a moment for context to initialize
        await new Promise(resolve => setTimeout(resolve, 100));
      }

      // Initialize if not already done
      if (!this.audioElement) {
        this.initAudio();
      }

      if (this.audioElement) {
        this.audioElement.currentTime = 0;
        this.audioElement.volume = 1.0;

        const playPromise = this.audioElement.play();
        if (playPromise !== undefined) {
          await playPromise;
        }

        console.log('✅ Notification sound played successfully');
      }
    } catch (err) {
      console.error('Failed to play notification sound:', err);

      // Fallback: Create a new audio element and try again
      try {
        const audio = new Audio(NOTIFICATION_SOUND_PATH);
        audio.volume = 1.0;
        audio.crossOrigin = 'anonymous';

        const playPromise = audio.play();
        if (playPromise !== undefined) {
          await playPromise;
        }

        console.log('✅ Notification sound played via fallback audio element');
      } catch (fallbackErr) {
        console.error('Fallback audio play also failed:', fallbackErr);
      }
    }
  }

  /**
   * Stop audio playback
   */
  stopAudio(): void {
    if (this.audioElement) {
      this.audioElement.pause();
      this.audioElement.currentTime = 0;
    }
  }

  /**
   * Cleanup
   */
  destroy(): void {
    if (this.audioElement) {
      this.audioElement.pause();
      this.audioElement.src = '';
      this.audioElement = null;
    }

    if (this.audioContext) {
      this.audioContext.close();
      this.audioContext = null;
    }
  }
}

// Export singleton instance
export const audioService = new AudioService();
